import { Layout } from '@/components/ui/layout';
import { translatePanelColumns } from './table/columns';
import { TranslatePanelDataTable } from './table/data-table';
import { PrivateAdminRoute } from '@/components/organisms/route/admin';
import { useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { QUERY_GET_ALL_TRANSLATIONS } from '@/graphql/queries/translate';
import { useLanguage } from '@/hooks/useLanguage';
import type { TranslatePanel, UpdateTranslationResponse } from '@/graphql/types/admin';
import { Ellipsis } from 'lucide-react';
import { Button, ButtonLoading } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { InputWithLabel } from '@/components/ui/input';
import { TextareaWithLabel } from '@/components/ui/textarea';
import { Form, Formik } from 'formik';
import * as yup from 'yup';
import { TranslatePanelTableLoading } from './table/loading';
import { MUTATION_UPDATE_TRANSLATION } from '@/graphql/mutation/translate';
import { Alert } from '@/components/molecules/alert';
import { useAlert } from '@/hooks/useAlert';

interface UpdateTranslationValues {
  key: string;
  language: string;
  value: string;
}

export default function TranslatePanelPage() {
  const apolloClient = useApolloClient();

  const { currentLanguage } = useLanguage();
  const { alert, showAlert, handleCloseAlert } = useAlert();

  const [loadingData, setLoadingData] = useState(false);
  const [loadingUpdate, setLoadingUpdate] = useState(false);
  const [data, setData] = useState<TranslatePanel[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState<TranslatePanel | null>(null);
  const [changedTranslations, setChangedTranslations] = useState<UpdateTranslationValues[]>([]);

  const schema = yup.object().shape({
    key: yup.string().required('Chave é obrigatória'),
    category: yup.string().required('Categoria é obrigatória'),
    subcategory: yup.string().required('Subcategoria é obrigatória'),
    translation_pt: yup.string().required('Tradução em português é obrigatória'),
    translation_en: yup.string().required('Tradução em inglês é obrigatória'),
  });

  const initialValues = {
    key: selectedItem?.key || '',
    count: selectedItem?.languageCount || 0,
    translation_pt:
      selectedItem?.translations.find((translation) => translation.language === 'pt-BR')?.value ||
      '',
    translation_en:
      selectedItem?.translations.find((translation) => translation.language === 'en-US')?.value ||
      '',
  };

  const normalizedData = useMemo(() => {
    return data.map((item) => ({
      key: item.key,
      translate_pt_br:
        item.translations.find((translation) => translation.language === 'pt-BR')?.value || '',
      translate_en_us:
        item.translations.find((translation) => translation.language === 'en-US')?.value || '',
      actions: (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="size-8">
              <Ellipsis />
            </Button>
          </DropdownMenuTrigger>

          <DropdownMenuContent side="bottom" align="end">
            <DropdownMenuItem
              onClick={() => {
                setOpenDialog(true);
                setSelectedItem(item);
              }}
            >
              <span>Editar</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    }));
  }, [data]);

  const handleTranslationChange = (
    field: 'translation_pt' | 'translation_en',
    value: string,
    formikHandleChange: (e: React.ChangeEvent<any>) => void
  ) => {
    formikHandleChange({
      target: { name: field, value },
    } as React.ChangeEvent<any>);

    if (!selectedItem) return;

    const language = field === 'translation_pt' ? 'pt-BR' : 'en-US';
    const originalValue =
      selectedItem.translations.find((translation) => translation.language === language)?.value ||
      '';

    const filteredChanges = changedTranslations.filter((change) => change.language !== language);

    if (value !== originalValue) {
      const newChange: UpdateTranslationValues = {
        key: selectedItem.key,
        language,
        value,
      };
      setChangedTranslations([...filteredChanges, newChange]);
    } else {
      setChangedTranslations(filteredChanges);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoadingUpdate(true);

      if (changedTranslations.length === 0) {
        showAlert('info', 'Nenhuma alteração foi detectada');
        setOpenDialog(false);
        setSelectedItem(null);
        setChangedTranslations([]);
        return;
      }

      const updatePromises = changedTranslations.map((input) =>
        apolloClient.mutate<{
          updateTranslation: UpdateTranslationResponse;
        }>({
          mutation: MUTATION_UPDATE_TRANSLATION,
          variables: { input },
        })
      );

      await Promise.all(updatePromises);

      showAlert('success', `${changedTranslations.length} tradução(ões) atualizada(s) com sucesso`);

      await getTranslations();

      setOpenDialog(false);
      setSelectedItem(null);
      setChangedTranslations([]);
    } catch (error) {
      error instanceof Error && showAlert('error', String(error).split('Error: ')[1]);
    } finally {
      setLoadingUpdate(false);
    }
  };

  const getTranslations = async () => {
    const { data } = await apolloClient.query<{
      getAllTranslations: TranslatePanel[];
    }>({
      query: QUERY_GET_ALL_TRANSLATIONS,
    });
    setData(data.getAllTranslations);
  };

  useEffect(() => {
    if (!openDialog || !selectedItem) {
      setChangedTranslations([]);
    }
  }, [openDialog, selectedItem]);

  useEffect(() => {
    const fetchData = async () => {
      setLoadingData(true);
      try {
        await getTranslations();
      } finally {
        setLoadingData(false);
      }
    };
    fetchData();
  }, [currentLanguage, apolloClient]);

  return (
    <PrivateAdminRoute>
      <Layout title="Painel de Traduções">
        {loadingData ? (
          <TranslatePanelTableLoading />
        ) : (
          <TranslatePanelDataTable columns={translatePanelColumns} data={normalizedData} />
        )}
      </Layout>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Tradução</DialogTitle>
          </DialogHeader>

          <DialogDescription>Edite as informações de traducção abaixo.</DialogDescription>

          <Formik
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validationSchema={schema}
            enableReinitialize
          >
            {({ values, handleChange, errors }) => (
              <Form className="flex flex-col gap-4">
                <InputWithLabel
                  label="Chave"
                  id="translation"
                  name="key"
                  defaultValue={values.key}
                  disabled
                />

                <TextareaWithLabel
                  label="Tradução em português*"
                  id="translation"
                  name="translation_pt"
                  rows={10}
                  defaultValue={values.translation_pt}
                  onChange={(e) =>
                    handleTranslationChange('translation_pt', e.target.value, handleChange)
                  }
                  error={Boolean(errors.translation_pt)}
                  helperText={errors.translation_pt}
                />

                <TextareaWithLabel
                  label="Tradução em inglês*"
                  id="translation"
                  name="translation_en"
                  rows={10}
                  defaultValue={values.translation_en}
                  onChange={(e) =>
                    handleTranslationChange('translation_en', e.target.value, handleChange)
                  }
                  error={Boolean(errors.translation_en)}
                  helperText={errors.translation_en}
                />

                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpenDialog(false)}>
                    Cancelar
                  </Button>

                  <ButtonLoading
                    variant="default"
                    label="Salvar alterações"
                    loading={loadingUpdate}
                    type="submit"
                    onClick={handleSubmit}
                    disabled={
                      loadingUpdate ||
                      changedTranslations.length === 0 ||
                      Boolean(errors.translation_pt) ||
                      Boolean(errors.translation_en)
                    }
                  />
                </DialogFooter>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      <Alert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        autoHideDuration={5000}
        onClose={handleCloseAlert}
      />
    </PrivateAdminRoute>
  );
}
